import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Phone, Wrench, Disc3, Search, Cog, Bike, ChevronDown, CircleDot,
} from "lucide-react";
import { SITE } from "@/lib/site";
import { Reveal, SectionHead } from "@/components/motion";
import SectionNav from "@/components/SectionNav";
import { useSEO } from "@/components/SEO";

const CATEGORIES = [
  { id: "tous", label: "Tous" },
  { id: "entretien", label: "Entretien" },
  { id: "freinage", label: "Freinage" },
  { id: "diagnostic", label: "Diagnostic" },
  { id: "mecanique", label: "Mécanique" },
  { id: "deux-roues", label: "Deux-roues" },
  { id: "nettoyage", label: "Nettoyage" },
];

const SERVICES = [
  {
    cat: "entretien", icon: Wrench, title: "Vidange & entretien périodique",
    vehicules: "Auto · Moto · Scooter",
    desc: "Vidange huile moteur, remplacement des filtres, contrôle des niveaux et des points de sécurité selon le carnet d'entretien de votre véhicule.",
    points: ["Huile & filtres", "Contrôle éclairage et pneus", "Mise à jour du carnet d'entretien"],
    tarif: "CHF 90.-/h — vidange simple avec filtre à huile : env. 1 h + consommables selon véhicule",
  },
  {
    cat: "entretien", icon: Wrench, title: "Révision toutes marques",
    vehicules: "Auto",
    desc: "Révision complète de votre véhicule, quelle que soit sa marque : chaque point est contrôlé avec minutie, soin et rigueur, pour que vous repartiez l'esprit tranquille.",
    points: ["Toutes marques & motorisations", "Contrôle point par point, travail soigné", "Devis détaillé avant travaux"],
    tarif: "Main-d'œuvre : CHF 90.-/h — devis personnalisé",
  },
  {
    cat: "entretien", icon: CircleDot, title: "Pneumatiques",
    vehicules: "Auto",
    desc: "Montage, équilibrage, permutation et contrôle de vos pneus, avec vérification de l'usure et des pressions.",
    points: ["Montage & équilibrage", "Permutation & contrôle d'usure", "Conseil sur le choix des pneus"],
    tarif: "dès CHF 15.- / roue — grille par diamètre ci-dessous",
  },
  {
    cat: "freinage", icon: Disc3, title: "Freinage complet",
    vehicules: "Auto · Moto · Scooter",
    desc: "Plaquettes, disques, purge du liquide de frein, durites : un freinage en bon état n'est pas négociable. Contrôle et remplacement avec pièces de qualité.",
    points: ["Plaquettes & disques", "Purge du liquide de frein", "Durites & étriers"],
    tarif: "CHF 90.-/h + pièces — le total dépend du véhicule et du prix des pièces",
  },
  {
    cat: "diagnostic", icon: Search, title: "Diagnostic électronique",
    vehicules: "Auto",
    desc: "Voyant allumé, perte de puissance, démarrage difficile ? Lecture des défauts à la valise multi-marques (automobile) et recherche de panne méthodique.",
    points: ["Lecture des codes défaut", "Recherche de panne électrique", "Batterie, alternateur, démarreur"],
    tarif: "Diagnostic : CHF 90.- · recherche de panne : CHF 90.-/h",
  },
  {
    cat: "mecanique", icon: Cog, title: "Mécanique générale",
    vehicules: "Auto",
    desc: "Distribution, embrayage, suspension, échappement, refroidissement : les réparations lourdes sont prises en charge avec un devis ferme avant intervention.",
    points: ["Distribution & courroies", "Embrayage & transmission", "Suspension & direction"],
    tarif: "Main-d'œuvre : CHF 90.-/h — devis personnalisé",
  },
  {
    cat: "deux-roues", icon: Bike, title: "Entretien moto",
    vehicules: "Moto",
    desc: "Révision complète de votre moto : kit chaîne, pneus, freins, vidange et contrôle général avant la saison ou après l'hivernage.",
    points: ["Kit chaîne & pneus moto", "Révision pré-saison", "Lift ciseaux : 1 voiture ou 2 deux-roues à la fois"],
  },
  {
    cat: "deux-roues", icon: Bike, title: "Entretien scooter",
    vehicules: "Scooter",
    desc: "Transmission, variateur, courroie, freins et pneus : votre scooter reste fiable pour vos trajets quotidiens entre Prilly et Lausanne.",
    points: ["Variateur & courroie", "Freinage & pneus", "Révision périodique"],
  },
  {
    cat: "mecanique", icon: Cog, title: "Préparation à l'expertise",
    vehicules: "Auto · Moto",
    desc: "Contrôle et mise en conformité de votre véhicule avant son passage à l'expertise vaudoise, pour éviter les contre-visites évitables.",
    points: ["Contrôle des points d'expertise", "Correction des défauts mineurs", "Conseils avant le rendez-vous"],
    tarif: "Contrôle : CHF 90.- · présentation : CHF 50.- · réparations sur devis",
  },
];

const FAQ = [
  {
    q: "Combien coûte un devis ?",
    a: "CHF 30.-, intégralement déduits de votre facture si vous validez les travaux. Vous décrivez votre besoin par le formulaire ou par téléphone, et vous recevez une estimation claire avant toute intervention — aucun travail n'est entrepris sans votre accord.",
  },
  {
    q: "Intervenez-vous sur toutes les marques ?",
    a: "Oui : voitures, motos et scooters de toutes marques sont les bienvenus. Précision utile : le diagnostic électronique à la valise concerne les voitures ; pour les deux-roues, la recherche de panne se fait de manière mécanique, par l'expérience.",
  },
  {
    q: "Combien de temps prend une intervention ?",
    a: "Le temps nécessaire dépend des travaux à effectuer : chaque véhicule et chaque panne sont différents. Un délai réaliste vous est toujours annoncé avec le devis, avant de commencer.",
  },
];

const PNEUS_TARIFS = [
  { diametre: "Jusqu'à 15 pouces", tole: "15.00 CHF", alu: "17.50 CHF", note: "Petites citadines, pneus légers." },
  { diametre: "16 à 17 pouces", tole: "17.50 CHF", alu: "20.00 CHF", note: "Voitures standards et berlines." },
  { diametre: "18 à 19 pouces", tole: "22.50 CHF", alu: "25.00 CHF", note: "Profils bas, demandent plus de précision." },
  { diametre: "20 à 22 pouces", tole: "27.50 CHF", alu: "30.00 CHF", note: "Gros SUV, roues lourdes à manipuler." },
  { diametre: "23 à 24 pouces", tole: "35.00 CHF", alu: "40.00 CHF", note: "Taille maximale, effort machine maximal." },
];

function PneusTable() {
  return (
    <div id="tarifs-pneus" className="mt-14 scroll-mt-36" data-testid="pneus-tarifs">
      <SectionHead eyebrow="Pneumatiques" title="Grille tarifaire — changement de pneus" />
      <Reveal>
        <div className="bg-white border border-[#D8D4CA] overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#121212] text-[#F6F3EB]">
                <th className="text-left font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-4">Diamètre de la jante</th>
                <th className="text-left font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-4">Prix par roue · jante tôle</th>
                <th className="text-left font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-4">Prix par roue · jante alu</th>
                <th className="text-left font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-4">Note technique</th>
              </tr>
            </thead>
            <tbody>
              {PNEUS_TARIFS.map((r, i) => (
                <tr key={r.diametre} data-testid={`pneus-row-${i}`} className="border-t border-[#EFECE4] hover:bg-[#F6F3EB] transition-colors">
                  <td className="px-5 py-4 font-medium text-[#121212]">{r.diametre}</td>
                  <td className="px-5 py-4 font-mono text-[#121212]">{r.tole}</td>
                  <td className="px-5 py-4 font-mono text-[#121212]">{r.alu}</td>
                  <td className="px-5 py-4 text-[#4A4D53]">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-[#9A968C]">
          Prix par roue, montage et équilibrage compris. Pneus non inclus — je peux vous les fournir
          sur devis. Exemple : 4 roues 16–17 pouces sur jantes alu = CHF 80.-.
        </p>
      </Reveal>
    </div>
  );
}

const NETTOYAGE_PACKS = [
  {
    name: "Pack Essentiel", price: "CHF 60.-", sub: "Nettoyage extérieur",
    includes: null,
    items: ["Prélavage", "Lavage carrosserie", "Nettoyage des jantes", "Rinçage", "Séchage", "Vitres extérieures"],
    ideal: "Idéal pour l'entretien courant du véhicule.",
    featured: false, testid: "pack-essentiel",
  },
  {
    name: "Pack Confort", price: "CHF 100.-", sub: "Intérieur + extérieur",
    includes: "Comprend le Pack Essentiel, plus :",
    items: ["Aspiration complète de l'habitacle", "Nettoyage des tapis", "Nettoyage des plastiques", "Nettoyage du tableau de bord", "Vitres intérieures", "Coffre"],
    ideal: "Idéal pour conserver un véhicule propre régulièrement.",
    featured: true, testid: "pack-confort",
  },
  {
    name: "Pack Premium", price: "CHF 160.-", sub: "Approfondi intérieur + extérieur",
    includes: "Comprend le Pack Confort, plus :",
    items: ["Nettoyage approfondi des plastiques", "Surfaces et détails de l'habitacle", "Nettoyage approfondi des tapis", "Traitement des surfaces intérieures", "Passages de portes", "Nettoyage approfondi des jantes", "Finition extérieure"],
    ideal: "Idéal avant une vente, une restitution ou une remise en état générale.",
    featured: false, testid: "pack-premium",
  },
];

const NETTOYAGE_COMPLEMENTS = [
  ["Nettoyage approfondi des sièges", "dès CHF 40.-"],
  ["Shampouinage tapis / moquettes", "dès CHF 40.-"],
  ["Nettoyage coffre approfondi", "dès CHF 20.-"],
  ["Nettoyage jantes approfondi", "dès CHF 20.-"],
  ["Traitement anti-odeurs", "dès CHF 30.-"],
  ["Poils d'animaux", "sur devis"],
  ["Nettoyage très intensif", "sur devis"],
];

const REMISE_ETAT_CASES = [
  "Sièges fortement tachés", "Tissus très sales", "Poils d'animaux", "Boue importante",
  "Odeurs persistantes", "Véhicule très négligé", "Après transport d'animaux",
  "Après travaux", "Autres situations particulières",
];

function NettoyageSection() {
  return (
    <div id="nettoyage" className="mt-14 scroll-mt-36" data-testid="nettoyage-section">
      <SectionHead index="✦" eyebrow="Nettoyage & préparation esthétique" title="Des forfaits clairs, un véhicule comme neuf" />
      <Reveal>
        <p className="max-w-2xl text-sm sm:text-base leading-relaxed text-[#4A4D53] -mt-4 mb-10">
          Des prestations adaptées aussi bien à l'entretien régulier qu'à la remise en état
          d'un véhicule avant une vente ou une restitution. Le nettoyage peut être demandé
          seul ou en complément d'une réparation ou d'un entretien — uniquement sur rendez-vous.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {NETTOYAGE_PACKS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.08} className="h-full">
            <article
              data-testid={p.testid}
              className={`h-full flex flex-col p-7 border transition-colors ${
                p.featured
                  ? "bg-[#121212] text-[#F6F3EB] border-[#121212]"
                  : "bg-white border-[#D8D4CA] hover:border-[#121212]"
              }`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B5D]">{p.sub}</p>
              <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-wide">{p.name}</h3>
              <p className={`mt-3 font-display text-3xl font-black ${p.featured ? "text-[#8C7B5D]" : "text-[#121212]"}`}>{p.price}</p>
              {p.includes && (
                <p className={`mt-4 text-xs ${p.featured ? "text-[#9A968C]" : "text-[#5A5D64]"}`}>{p.includes}</p>
              )}
              <ul className="mt-4 space-y-2">
                {p.items.map((it) => (
                  <li key={it} className={`flex gap-2 text-sm ${p.featured ? "text-[#C9C5B8]" : "text-[#4A4D53]"}`}>
                    <span className="h-1 w-3 bg-[#8C7B5D] mt-2 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
              <p className={`mt-5 pt-4 border-t text-xs italic mt-auto ${p.featured ? "border-[#242529] text-[#9A968C]" : "border-[#EFECE4] text-[#5A5D64]"}`}>
                {p.ideal}
              </p>
              <Link
                to="/devis"
                data-testid={`${p.testid}-cta`}
                className={`mt-5 inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-3.5 transition-colors ${
                  p.featured
                    ? "bg-[#8C7B5D] text-[#121212] hover:bg-[#F6F3EB]"
                    : "bg-[#121212] text-[#F6F3EB] hover:bg-[#8C7B5D] hover:text-[#121212]"
                }`}
              >
                Réserver ce pack <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-5 bg-white border border-[#D8D4CA] p-7" data-testid="pack-remise-en-etat">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-xl font-bold uppercase tracking-wide text-[#121212]">Pack Remise en état</h3>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C7B5D] border border-[#D8D4CA] px-3 py-1.5">Sur devis</span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[#4A4D53]">
            Pour les véhicules nécessitant un nettoyage particulier. Le prix est déterminé
            après évaluation de l'état du véhicule.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {REMISE_ETAT_CASES.map((c) => (
              <span key={c} className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#4A4D53] bg-[#F6F3EB] border border-[#D8D4CA] px-3 py-1.5">
                {c}
              </span>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Reveal className="h-full">
          <div className="h-full bg-white border border-[#D8D4CA] p-7" data-testid="nettoyage-complements">
            <h3 className="font-display text-xl font-bold uppercase tracking-wide text-[#121212]">Prestations complémentaires</h3>
            <ul className="mt-4 divide-y divide-[#EFECE4]">
              {NETTOYAGE_COMPLEMENTS.map(([label, price]) => (
                <li key={label} className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
                  <span className="text-[#4A4D53]">{label}</span>
                  <span className="font-mono text-xs text-[#121212] whitespace-nowrap">{price}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.08} className="h-full">
          <div className="h-full bg-[#121212] text-[#F6F3EB] p-7 texture-grain" data-testid="nettoyage-moto">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B5D]">Deux-roues</p>
            <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-wide">Nettoyage moto & scooter</h3>
            <div className="mt-5 space-y-4">
              <div className="flex items-baseline justify-between gap-4 border-b border-[#242529] pb-4">
                <div>
                  <p className="text-sm font-medium">Nettoyage extérieur</p>
                  <p className="text-xs text-[#9A968C] mt-1">Lavage, jantes, séchage, finition</p>
                </div>
                <span className="font-display text-xl font-bold text-[#8C7B5D] whitespace-nowrap">CHF 40.-</span>
              </div>
              <div className="flex items-baseline justify-between gap-4 border-b border-[#242529] pb-4">
                <div>
                  <p className="text-sm font-medium">Nettoyage complet</p>
                  <p className="text-xs text-[#9A968C] mt-1">Extérieur + bulle / vitres + surfaces accessibles</p>
                </div>
                <span className="font-display text-xl font-bold text-[#8C7B5D] whitespace-nowrap">CHF 70.-</span>
              </div>
              <p className="text-xs text-[#9A968C]">Nettoyage intensif : sur devis.</p>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <p className="mt-6 text-xs leading-relaxed text-[#9A968C] border-l-2 border-[#8C7B5D] pl-4 max-w-3xl" data-testid="nettoyage-note">
          Les forfaits de nettoyage sont prévus pour des véhicules dans un état d'utilisation normal.
          Un supplément peut être proposé après évaluation pour les véhicules présentant une saleté
          exceptionnelle, des taches importantes, des poils d'animaux, des odeurs persistantes ou
          nécessitant un temps de travail supplémentaire. Aucun supplément n'est appliqué sans accord du client.
        </p>
      </Reveal>
    </div>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#D8D4CA] bg-white" data-testid={testid}>
      <button
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display font-bold uppercase tracking-wide text-base text-[#121212]">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#8C7B5D] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="px-6 pb-5 text-sm leading-relaxed text-[#4A4D53]">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  useSEO(
    "Services — Entretien, freinage, diagnostic auto, moto & scooter à Prilly | BM MECHANICS",
    "Entretien, freinage, pneus, diagnostic et mécanique pour auto, moto et scooter à Prilly, près de Lausanne. Devis CHF 30.-, déduit des travaux."
  );
  const [cat, setCat] = useState("tous");
  const list = cat === "tous" ? SERVICES : SERVICES.filter((s) => s.cat === cat);

  return (
    <div data-testid="services-page">
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-16 md:pt-40 md:pb-20 texture-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">Services</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              Entretien, réparation<br />& diagnostic
            </h1>
            <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#9A968C]">
              Chaque intervention démarre par un devis à CHF 30.-, intégralement déduit
              de la facture si vous validez les travaux. Main-d'œuvre : CHF 90.-/heure,
              pièces et consommables en sus selon le véhicule — jamais de surprise sur la facture.
            </p>
          </Reveal>
        </div>
      </section>

      <SectionNav
        items={[
          { id: "prestations", label: "Prestations" },
          { id: "tarifs-pneus", label: "Tarifs pneus" },
          { id: "nettoyage", label: "Nettoyage" },
          { id: "faq", label: "Questions fréquentes" },
        ]}
      />

      <section id="prestations" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="services-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`service-tab-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-2.5 border transition-colors ${
                  cat === c.id
                    ? "bg-[#121212] text-[#F6F3EB] border-[#121212]"
                    : "bg-white text-[#4A4D53] border-[#D8D4CA] hover:border-[#121212] hover:text-[#121212]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {cat !== "nettoyage" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="services-grid">
            {list.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.08} className="h-full">
                <article className="h-full bg-white border border-[#D8D4CA] hover:border-[#121212] transition-colors p-7 flex flex-col" data-testid={`service-detail-${s.cat}-${i}`}>
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-11 w-11 items-center justify-center bg-[#121212] text-[#8C7B5D]">
                      <s.icon className="h-5 w-5" />
                    </span>
                    <div className="flex flex-col items-end gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B5D] border border-[#D8D4CA] px-3 py-1.5">
                        {s.vehicules}
                      </span>
                      {s.rdv && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#121212] bg-[#8C7B5D]/20 border border-[#8C7B5D]/50 px-3 py-1.5">
                          Sur rendez-vous
                        </span>
                      )}
                    </div>
                  </div>
                  <h2 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-[#121212]">{s.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A4D53]">{s.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-[#4A4D53]">
                        <span className="h-1 w-3 bg-[#8C7B5D]" /> {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-[#EFECE4] flex items-center justify-between gap-3 mt-auto">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#9A968C]">
                      {s.tarif || "Tarif : sur devis"}
                    </span>
                    <Link
                      to="/devis"
                      data-testid={`service-cta-${s.cat}-${i}`}
                      className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#121212] hover:text-[#8C7B5D] transition-colors"
                    >
                      Devis <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          )}

          {(cat === "tous" || cat === "entretien") && <PneusTable />}

          {(cat === "tous" || cat === "nettoyage") && <NettoyageSection />}
        </div>
      </section>

      <section id="faq" className="py-16 md:py-20 bg-[#EFECE4] hairline-t hairline-b" data-testid="services-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="FAQ" eyebrow="Questions fréquentes" title="Ce qu'on nous demande souvent" />
          <div className="space-y-3">
            {FAQ.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} testid={`faq-item-${i}`} />)}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/devis"
                data-testid="services-quote-btn"
                className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
              >
                Demander un devis <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="services-call-btn"
                className="inline-flex items-center gap-2 border border-[#121212] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#121212] hover:text-[#F6F3EB] transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
